import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import axios from "axios";
import "./styles/App.css";

const API_BASE = process.env.REACT_APP_API_URL || "";

const ACCEPTANCE_INPUT = `Business: LedgerWing
Offer: AI-assisted invoice reconciliation for recruitment agencies
Audience: UK recruitment agencies with 20 to 200 employees
Region: United Kingdom
Launch goal: book 15 qualified product demonstrations within 30 days
Core problem: finance teams spend too much time matching contractor invoices, timesheets and client billing records
Differentiator: flags mismatches before payroll and client invoicing deadlines
Constraints: use evidence-backed claims, do not invent customer results, avoid spam language, and keep the campaign suitable for professional B2B outreach`;

function getOwnerId() {
  const existing = window.localStorage.getItem("signallaunch_owner_id");
  if (existing) return existing;
  const next = `owner_${window.crypto?.randomUUID?.() || Date.now()}`;
  window.localStorage.setItem("signallaunch_owner_id", next);
  return next;
}

function statusLabel(campaign) {
  if (!campaign) return "Preparing";
  if (campaign.execution?.status === "completed") return "Campaign ready";
  if (campaign.payment?.status === "paid") return "Campaign in production";
  if (campaign.payment?.status === "checkout_created") return "Checkout ready";
  return "Saved campaign";
}

function SourcePills({ summary }) {
  if (!summary) return null;
  const counts = summary.providerCounts || {};
  return (
    <div className="source-pills" aria-label="Source summary">
      <span>{summary.totalRecords || summary.sourceCount || 0} sources</span>
      <span>HN {counts.hn_algolia || 0}</span>
      <span>Wikipedia {counts.wikipedia_mediawiki || 0}</span>
    </div>
  );
}

function AppShell({ children }) {
  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/" className="brand">
          <span className="brand-mark">AW</span>
          <span>
            <strong>SignalLaunch</strong>
            <small>Revenue Campaign Engine</small>
          </span>
        </Link>
        <nav>
          <Link to="/history">History</Link>
        </nav>
      </header>
      {children}
    </div>
  );
}

function HomePage() {
  const [ownerId] = useState(getOwnerId);
  const [rawInput, setRawInput] = useState(ACCEPTANCE_INPUT);
  const [preview, setPreview] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const selectedDirection = useMemo(() => {
    return preview?.preview?.directions?.find((direction) => direction.id === selectedId);
  }, [preview, selectedId]);

  async function createPreview() {
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      const response = await axios.post(`${API_BASE}/api/signallaunch/preview`, {
        rawInput,
        ownerId,
      });
      setPreview(response.data);
      setSelectedId(response.data.preview.directions[0]?.id || null);
    } catch (err) {
      setError(err.response?.data?.message || "Preview could not be created.");
    } finally {
      setBusy(false);
    }
  }

  async function saveCampaign() {
    if (!preview || !selectedDirection) return;
    setSaving(true);
    setError("");
    try {
      const response = await axios.post(`${API_BASE}/api/signallaunch/campaigns`, {
        ownerId,
        previewId: preview.previewId,
        selectedDirectionId: selectedDirection.id,
      });
      navigate(`/campaign/${response.data.campaign.id}`);
    } catch (err) {
      setError(err.response?.data?.message || "Campaign could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell>
      <main className="workspace">
        <section className="input-panel">
          <div className="section-kicker">B2B distribution campaign</div>
          <h1>Paste one offer brief. Choose a source-backed launch direction.</h1>
          <p className="lead">
            SignalLaunch turns a concise offer brief into a campaign direction,
            then produces the full launch package after checkout.
          </p>
          <textarea
            value={rawInput}
            onChange={(event) => setRawInput(event.target.value)}
            spellCheck="true"
            aria-label="Offer brief"
          />
          <button className="primary-action" onClick={createPreview} disabled={busy}>
            {busy ? "Creating preview..." : "Create preview"}
          </button>
          {error && <div className="error-banner">{error}</div>}
        </section>

        <section className="preview-panel">
          {!preview && (
            <div className="empty-state">
              <h2>Preview appears here</h2>
              <p>Market context, evidence boundaries and three directions will be ready from one action.</p>
            </div>
          )}

          {preview && (
            <>
              <div className="panel-heading">
                <div>
                  <div className="section-kicker">Decision preview</div>
                  <h2>Three campaign directions</h2>
                </div>
                <SourcePills summary={preview.preview.sourceSummary} />
              </div>

              <div className="context-list">
                {preview.preview.sourceBackedMarketContext.map((item) => (
                  <p key={item}>{item}</p>
                ))}
              </div>

              <div className="direction-grid">
                {preview.preview.directions.map((direction) => (
                  <button
                    key={direction.id}
                    className={`direction-card ${selectedId === direction.id ? "selected" : ""}`}
                    onClick={() => setSelectedId(direction.id)}
                  >
                    <span className="rank">Rank {direction.rank}</span>
                    <strong>{direction.name}</strong>
                    <p>{direction.suggestedPromise}</p>
                    <small>{direction.uncertainty}</small>
                  </button>
                ))}
              </div>

              <div className="evidence-strip">
                {selectedDirection?.sourceRefs?.slice(0, 4).map((source) => (
                  <a href={source.url} target="_blank" rel="noreferrer" key={source.url}>
                    <span>{source.provider}</span>
                    {source.title}
                  </a>
                ))}
              </div>

              <div className="checkout-transition">
                <div>
                  <strong>{selectedDirection?.name}</strong>
                  <p>{selectedDirection?.rationale}</p>
                </div>
                <button className="primary-action" onClick={saveCampaign} disabled={saving}>
                  {saving ? "Saving campaign..." : "Save campaign"}
                </button>
              </div>
            </>
          )}
        </section>
      </main>
    </AppShell>
  );
}

function CampaignDetailPage() {
  const { campaignId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const campaignLoaded = Boolean(campaign);
  const pollingStatus = campaign?.execution?.status;

  const loadCampaign = useCallback(async () => {
    const response = await axios.get(`${API_BASE}/api/signallaunch/campaigns/${campaignId}`);
    setCampaign(response.data.campaign);
  }, [campaignId]);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");
        if (params.get("checkout") === "success" && sessionId) {
          setMessage("Verifying checkout and starting production...");
          const verifyResponse = await axios.post(
            `${API_BASE}/api/signallaunch/campaigns/${campaignId}/verify-checkout`,
            { sessionId }
          );
          if (!cancelled) {
            setCampaign(verifyResponse.data.campaign);
            setMessage("Payment verified. Campaign in production.");
            navigate(`/campaign/${campaignId}`, { replace: true });
          }
        } else {
          await loadCampaign();
        }
      } catch (err) {
        setError(err.response?.data?.message || err.response?.data?.error || "Campaign could not be loaded.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    boot();
    return () => {
      cancelled = true;
    };
  }, [campaignId, loadCampaign, location.search, navigate]);

  useEffect(() => {
    if (!campaignLoaded || pollingStatus === "completed") return undefined;
    const interval = window.setInterval(async () => {
      try {
        await loadCampaign();
      } catch (err) {
        setError("Status refresh failed.");
      }
    }, 4000);
    return () => window.clearInterval(interval);
  }, [campaignLoaded, loadCampaign, pollingStatus]);

  useEffect(() => {
    async function loadHistory() {
      const ownerId = campaign?.ownerId || getOwnerId();
      try {
        const response = await axios.get(`${API_BASE}/api/signallaunch/campaigns`, {
          params: { ownerId },
        });
        setHistory(response.data.campaigns || []);
      } catch {
        setHistory([]);
      }
    }
    if (campaign) loadHistory();
  }, [campaign]);

  async function openCheckout() {
    setError("");
    setMessage("Preparing checkout...");
    try {
      const response = await axios.post(`${API_BASE}/api/signallaunch/campaigns/${campaignId}/checkout`);
      window.location.href = response.data.checkoutUrl;
    } catch (err) {
      setError(err.response?.data?.message || "Checkout could not be opened.");
      setMessage("");
    }
  }

  if (loading && !campaign) {
    return (
      <AppShell>
        <main className="detail-shell">
          <div className="empty-state">Loading campaign...</div>
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <main className="detail-shell">
        {error && <div className="error-banner">{error}</div>}
        {message && <div className="notice-banner">{message}</div>}

        {campaign && (
          <>
            <section className="detail-hero">
              <div>
                <div className="section-kicker">Campaign Detail</div>
                <h1>{campaign.seed.business} launch campaign</h1>
                <p>{campaign.selectedDirection?.suggestedPromise}</p>
              </div>
              <div className="status-stack">
                <span>{statusLabel(campaign)}</span>
                <strong>{campaign.execution?.percentComplete || 0}%</strong>
              </div>
            </section>

            <section className="summary-grid">
              <div>
                <span>Payment</span>
                <strong>{campaign.payment?.status === "paid" ? "Verified" : "Not completed"}</strong>
              </div>
              <div>
                <span>Production</span>
                <strong>{campaign.execution?.currentStage || "Ready"}</strong>
              </div>
              <div>
                <span>Sources</span>
                <strong>{campaign.sourceArtifacts?.postPaymentSourceCount || campaign.engine1Preview?.sourceSummary?.totalRecords || 0}</strong>
              </div>
              <div>
                <span>Monitoring</span>
                <strong>{campaign.monitoring?.status || "Waiting"}</strong>
              </div>
            </section>

            {campaign.payment?.status !== "paid" && (
              <section className="checkout-band">
                <div>
                  <h2>Ready for full campaign production</h2>
                  <p>
                    Checkout unlocks the full source-backed campaign package,
                    live launch hub and first monitoring cycle.
                  </p>
                </div>
                <button className="primary-action" onClick={openCheckout}>
                  Continue to checkout
                </button>
              </section>
            )}

            <section className="content-columns">
              <article className="dark-panel">
                <div className="section-kicker">Selected direction</div>
                <h2>{campaign.selectedDirection?.name}</h2>
                <p>{campaign.selectedDirection?.rationale}</p>
                <p>{campaign.selectedDirection?.uncertainty}</p>
              </article>

              <article className="dark-panel">
                <div className="section-kicker">Source acquisition</div>
                <h2>Evidence summary</h2>
                <SourcePills summary={campaign.engine1Preview?.sourceSummary} />
                {campaign.sourceArtifacts?.postPaymentProviderCounts && (
                  <div className="source-pills">
                    <span>Production {campaign.sourceArtifacts.postPaymentSourceCount}</span>
                    <span>HN {campaign.sourceArtifacts.postPaymentProviderCounts.hn_algolia || 0}</span>
                    <span>Wikipedia {campaign.sourceArtifacts.postPaymentProviderCounts.wikipedia_mediawiki || 0}</span>
                  </div>
                )}
              </article>
            </section>

            {campaign.engine2?.package && (
              <section className="package-section">
                <div className="panel-heading">
                  <div>
                    <div className="section-kicker">Campaign package</div>
                    <h2>{campaign.engine2.package.landingPageCopy.headline}</h2>
                  </div>
                  {campaign.engine3?.publicUrl && (
                    <a className="secondary-action" href={campaign.engine3.publicUrl} target="_blank" rel="noreferrer">
                      Open launch hub
                    </a>
                  )}
                </div>

                <div className="output-grid">
                  <OutputBlock title="Positioning" items={[campaign.engine2.package.selectedPositioning]} />
                  <OutputBlock title="Buyer Segments" items={campaign.engine2.package.targetBuyerSegments.map((item) => `${item.segment}: ${item.message}`)} />
                  <OutputBlock title="Landing Copy" items={[
                    campaign.engine2.package.landingPageCopy.subhead,
                    ...campaign.engine2.package.landingPageCopy.bullets,
                  ]} />
                  <OutputBlock title="Seven-Day Sequence" items={campaign.engine2.package.sevenDayContentSequence} />
                  <OutputBlock title="Outreach Messages" items={campaign.engine2.package.outreachMessages.map((item) => `${item.subject}: ${item.body}`)} />
                  <OutputBlock title="Objection Handling" items={campaign.engine2.package.objectionHandling.map((item) => `${item.objection}: ${item.response}`)} />
                  <OutputBlock title="Channel Plan" items={campaign.engine2.package.channelPlan} />
                  <OutputBlock title="Next Actions" items={campaign.engine2.package.nextActionPlan} />
                </div>
              </section>
            )}

            {campaign.monitoring && (
              <section className="dark-panel">
                <div className="section-kicker">Monitoring</div>
                <h2>{campaign.monitoring.result}</h2>
                <ul className="clean-list">
                  {(campaign.monitoring.notableSignals || []).slice(0, 6).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            )}

            <section className="history-section">
              <div className="panel-heading">
                <div>
                  <div className="section-kicker">History</div>
                  <h2>Recoverable campaigns</h2>
                </div>
              </div>
              <div className="history-list">
                {history.map((item) => (
                  <Link to={`/campaign/${item.id}`} key={item.id}>
                    <strong>{item.seed?.business || "Campaign"}</strong>
                    <span>{statusLabel(item)}</span>
                  </Link>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </AppShell>
  );
}

function OutputBlock({ title, items }) {
  return (
    <article className="output-block">
      <h3>{title}</h3>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </article>
  );
}

function HistoryPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const response = await axios.get(`${API_BASE}/api/signallaunch/campaigns`, {
          params: { ownerId: getOwnerId() },
        });
        setCampaigns(response.data.campaigns || []);
      } catch (err) {
        setError("History could not be loaded.");
      }
    }
    load();
  }, []);

  return (
    <AppShell>
      <main className="detail-shell">
        <div className="section-kicker">History</div>
        <h1>Campaign history</h1>
        {error && <div className="error-banner">{error}</div>}
        <div className="history-list full">
          {campaigns.map((campaign) => (
            <Link to={`/campaign/${campaign.id}`} key={campaign.id}>
              <strong>{campaign.seed?.business || "Campaign"}</strong>
              <span>{statusLabel(campaign)}</span>
            </Link>
          ))}
        </div>
      </main>
    </AppShell>
  );
}

function PublicHubPage() {
  const { slug } = useParams();
  const [hub, setHub] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const response = await axios.get(`${API_BASE}/api/signallaunch/public/${slug}`);
        setHub(response.data.hub);
      } catch {
        setError("Launch hub could not be loaded.");
      }
    }
    load();
  }, [slug]);

  return (
    <AppShell>
      <main className="public-shell">
        {error && <div className="error-banner">{error}</div>}
        {hub && (
          <>
            <section className="public-hero">
              <div className="section-kicker">Launch hub</div>
              <h1>{hub.title}</h1>
              <p>{hub.summary}</p>
            </section>
            <section className="output-grid">
              <OutputBlock title="Campaign Promise" items={[hub.package.campaignPromise]} />
              <OutputBlock title="Target Buyers" items={hub.package.targetBuyerSegments.map((item) => `${item.segment}: ${item.message}`)} />
              <OutputBlock title="Landing Copy" items={[
                hub.package.landingPageCopy.headline,
                hub.package.landingPageCopy.subhead,
                ...hub.package.landingPageCopy.bullets,
              ]} />
              <OutputBlock title="Source Dossier" items={hub.package.sourceDossier.slice(0, 8).map((item) => `${item.provider}: ${item.title}`)} />
            </section>
            {hub.monitoring && (
              <section className="dark-panel">
                <div className="section-kicker">Monitoring</div>
                <h2>{hub.monitoring.result}</h2>
              </section>
            )}
          </>
        )}
      </main>
    </AppShell>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/campaign/:campaignId" element={<CampaignDetailPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/launch/:slug" element={<PublicHubPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
